import { gql, useMutation } from '@apollo/client';
import {
  IPartyForUser,
  IPartyStageForUser,
  StreamType,
  TUserId,
} from '../../../PartyClientResponse';
import { Button, Input, Tooltip, Form, Select } from 'antd';
import React, { useState, useEffect } from 'react';
import InputRowItem from '../../common/InputRowItem';
import { useAppState, ActionType } from '../../../state/AppState';
import { $enum } from 'ts-enum-util';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Maybe } from 'graphql/jsutils/Maybe';
import Modal from '../../../components/common/Modal';
import {} from '@ant-design/icons';
import _ from 'lodash';

const getMostRecentBackroomStream = (party: IPartyForUser) => {
  if (party.stage.stream.type === StreamType.Backroom) {
    const { backroomStreamId, audioSource } = party.stage.stream;
    return {
      backroomStreamId,
      audioSource,
    };
  }

  const hist = party.stage.streamHistory || [];

  for (let i = 0; i < hist.length; i++) {
    const h = hist[i];
    if (h.type === StreamType.Backroom) {
      return {
        backroomStreamId: h.backroomStreamId,
        audioSource: h.audioSource,
      };
    }
  }

  return {
    backroomStreamId: '',
    audioSource: '',
  };
};

const { Option } = Select;

const SettingsSection = ({ children, title, ...props }: { children: any; title: string }) => (
  <div className='settings-section'>
    <h3 {...props}>
      {title} {}
    </h3>
    {children}
  </div>
);

const HostControls = ({
  stage,
  party,
  localVideoTimestamp,
  debug,
  addFakeUser,
}: {
  debug: boolean;
  stage: Maybe<IPartyStageForUser>;
  party: IPartyForUser;
  localVideoTimestamp: number;
  addFakeUser: () => void;
}) => {
  const { user, isSharingScreen, dispatch } = useAppState();
  const [streamType, setStreamType] = useState<StreamType>(
    stage?.stream?.type || StreamType.Twitch
  );
  const initialTwitchId =
    stage?.stream?.type === StreamType.Twitch
      ? stage.stream.twitchId
      : localStorage.getItem('KEY_PREVIOUS_TWITCH') || '';

  const initialBackroomStream = getMostRecentBackroomStream(party);

  const [twitchId, setTwitchId] = useState<string>(initialTwitchId);
  const [backroomStreamId, setBackroomStreamId] = useState<string>(
    initialBackroomStream.backroomStreamId
  );
  const [audioSource, setAudioSource] = useState<string>(initialBackroomStream.audioSource);
  const initialYoutubeId = stage?.stream?.type === StreamType.Youtube ? stage.stream.youtubeId : '';
  const [youtubeId, setSetYoutubeId] = useState<string>(initialYoutubeId);
  const initialMainParticipantId =
    stage?.stream?.type === StreamType.Participant ? stage.stream.mainParticipantId : '';
  const [mainParticipantId, setMainParticipantId] = useState<string>(initialMainParticipantId);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [form] = Form.useForm();

  const channels = party.stage.streamKeys || [];
  const currentParticipant = stage?.participants[user?.uid as TUserId];
  const isHost = user?.isAdmin || currentParticipant?.tags?.includes('host');

  useEffect(() => {
    if (streamType !== StreamType.Screenshare && isSharingScreen) {
      dispatch({ type: ActionType.SetShareScreen, isSharingScreen: false });
    }
  }, [streamType, isSharingScreen, dispatch]);

  const [, { loading }] = useMutation(gql`
    mutation EditVideoTimestamp($key: String!, $playbackTimestamp: Float) {
      updateParty(input: { key: $key, playbackTimestamp: $playbackTimestamp }) {
        success
      }
    }
  `);

  const [clearChat, { loading: clearChatLoading }] = useMutation(gql`
    mutation ClearChat($key: String!) {
      clearChat(input: { partyKey: $key }) {
        success
      }
    }
  `);

  const [updateParty, { loading: partyUpdateLoading }] = useMutation(
    gql`
      mutation EditPartyStage(
        $key: String!
        $streamType: Int
        $twitchId: String
        $youtubeId: String
        $mainParticipantId: String
        $muteAllParticipants: Boolean
        $links: [PartyStageLinkInput]
        $backroomStreamId: String
        $audioSource: String
      ) {
        updateParty(
          input: {
            key: $key
            streamType: $streamType
            youtubeId: $youtubeId
            twitchId: $twitchId
            mainParticipantId: $mainParticipantId
            muteAllParticipants: $muteAllParticipants
            links: $links
            backroomStreamId: $backroomStreamId
            audioSource: $audioSource
          }
        ) {
          success
        }
      }
    `
  );

  if (!isHost) {
    return <div />;
  }

  return (
    <>
      {user?.isAdmin && (
        <div className='floating-control-button info' onClick={addFakeUser}>
          <img alt='settings' src='/icons/invite_user.svg' width={40} height={40}></img>
        </div>
      )}
      <Tooltip arrowPointAtCenter title='View host controls' placement='bottom'>
        <div className='floating-control-button info' onClick={() => setExpanded(!expanded)}>
          <img alt='settings' src='/icons/settings_host.svg' width={40} height={40}></img>
        </div>
      </Tooltip>
      <Modal isOpen={expanded} onAfterClose={() => setExpanded(false)}>
        <div className='onboarding-card'>
          <Form
            form={form}
            name='update-party-form'
            onFinish={() => {}}
            initialValues={stage || {}}>
            <h1 style={{ textAlign: 'center' }}>Host controls</h1>
            <div className='update-party-card'>
              <div style={{ marginTop: 24, width: 500 }}>
                <SettingsSection title='stream source'>
                  <InputRowItem
                    left={
                      <Select
                        value={$enum(StreamType).getKeyOrDefault(streamType)}
                        onSelect={(value) => setStreamType(StreamType[value])}
                        style={{ width: '100%' }}>
                        {$enum(StreamType)
                          .getEntries()
                          .map(([key, value]) => (
                            <Select.Option key={key} value={key}>
                              {key}
                            </Select.Option>
                          ))}
                      </Select>
                    }
                    right='stream source'
                  />
                  {streamType === StreamType.Backroom && (
                    <>
                      <InputRowItem
                        left={
                          <Select
                            value={backroomStreamId}
                            onSelect={(value) => setBackroomStreamId(value)}
                            style={{ width: '100%' }}>
                            {_.uniq(
                              party.stage.streamHistory
                                .filter((h) => h.type === StreamType.Backroom)
                                .map((h) => {
                                  if (h.type === StreamType.Backroom) {
                                    return h.backroomStreamId;
                                  }
                                  return undefined;
                                })
                                .filter((id) => id !== undefined)
                            ).map((id) => {
                              return (
                                <Select.Option key={id} value={id || ''}>
                                  {id}
                                </Select.Option>
                              );
                            })}
                          </Select>
                        }
                        right='stream id'
                      />
                      <InputRowItem
                        left={
                          <Select
                            value={audioSource}
                            onSelect={(value) => setAudioSource(value)}
                            style={{ width: '100%' }}>
                            <Select.Option value='audio-follows-video'>
                              Audio follows video
                            </Select.Option>
                            {channels.map((channel) => (
                              <Select.Option value={channel}>{channel}</Select.Option>
                            ))}
                          </Select>
                        }
                        right='audio source'
                      />
                    </>
                  )}
                  {streamType === StreamType.Twitch && (
                    <InputRowItem
                      left={
                        <Input
                          value={twitchId}
                          onChange={(event) => setTwitchId(event.target.value)}
                          autoFocus={true}
                          style={{ textAlign: 'center' }}
                        />
                      }
                      right='twitch id'
                    />
                  )}
                  {streamType === StreamType.Youtube && (
                    <InputRowItem
                      left={
                        <Input
                          value={youtubeId}
                          onChange={(event) => setSetYoutubeId(event.target.value)}
                          autoFocus={true}
                          style={{ textAlign: 'center' }}
                        />
                      }
                      right='youtube id'
                    />
                  )}
                  {streamType === StreamType.Participant && (
                    <InputRowItem
                      left={
                        <Select
                          value={mainParticipantId}
                          showSearch
                          placeholder='Select a person'
                          optionFilterProp='children'
                          onChange={(value) => setMainParticipantId(value)}
                          style={{ width: '100%' }}
                          filterOption={(input, option) =>
                            option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }>
                          {Object.values(party?.stage?.participants || {})
                            .filter((p) => p.active)
                            .map((p) => (
                              <Option key={p.uid} value={p.uid}>
                                {p.displayName}
                              </Option>
                            ))}
                        </Select>
                      }
                      right='guest'
                    />
                  )}
                </SettingsSection>

                <SettingsSection title='links'>
                  <Form.List name='links'>
                    {(fields, { add, remove }) => (
                      <div>
                        {fields.map((link) => (
                          <div
                            key={link.key}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginBottom: 12,
                            }}>
                            <Form.Item
                              noStyle
                              // style={{ display: 'inline-flex', flexGrow: 1, paddingRight: 12 }}
                              name={[link.name, 'url']}
                              fieldKey={[link.fieldKey, 'url']}>
                              <Input style={{ marginRight: 12 }} type='text' placeholder='URL' />
                            </Form.Item>
                            <Form.Item
                              noStyle
                              // style={{ display: 'inline-flex', flexGrow: 1, paddingRight: 12 }}
                              name={[link.name, 'text']}
                              fieldKey={[link.fieldKey, 'text']}>
                              <Input
                                style={{ marginRight: 12 }}
                                type='text'
                                placeholder='Link text'
                              />
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(link.name)} />
                          </div>
                        ))}
                        <Button
                          onClick={() => {
                            add({ key: Math.random() });
                          }}>
                          <PlusOutlined /> add
                        </Button>
                      </div>
                    )}
                  </Form.List>
                </SettingsSection>

                <SettingsSection title='actions'>
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}>
                    <Button
                      type='dashed'
                      loading={loading}
                      style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        display: 'inline-flex',
                        width: '48%',
                      }}
                      onClick={() => {
                        updateParty({
                          variables: {
                            key: party.key,
                            muteAllParticipants: !stage?.muteAllParticipants,
                          },
                        });
                      }}>
                      {stage?.muteAllParticipants
                        ? 'unmute all participants'
                        : 'mute all participants'}
                    </Button>
                    <Tooltip
                      arrowPointAtCenter
                      title='Enter Backroom cloud studio mode'
                      placement='top'>
                      <Button
                        type='dashed'
                        loading={loading}
                        style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                          display: 'inline-flex',
                          width: '48%',
                        }}
                        onClick={() => {
                          dispatch({ type: ActionType.ToggleCockpitMode, enable: true });
                        }}>
                        Open Cloud Studio
                      </Button>
                    </Tooltip>
                  </div>
                  {user?.isAdmin && (
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}>
                      <Button
                        type='dashed'
                        loading={clearChatLoading}
                        style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                          display: 'inline-flex',
                          width: '48%',
                          marginTop: 20,
                        }}
                        onClick={() => {
                          clearChat({
                            variables: {
                              key: party.key,
                            },
                          });
                        }}>
                        clear chat
                      </Button>
                    </div>
                  )}
                  {/* <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: 40,
                    }}> */}
                  {/* <Button
                type='dashed'
                loading={loading}
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: 'inline-flex',
                  width: '48%',
                }}
                onClick={() => {
                  updateVideoTimestamp({
                    variables: {
                      key: party.key,
                      playbackTimestamp: localVideoTimestamp,
                    },
                  });
                }}>
                sync
              </Button> */}
                  {/* </div> */}
                </SettingsSection>
              </div>
            </div>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: 40,
              }}>
              <Button
                type='dashed'
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: 'inline-flex',
                  width: '48%',
                  height: 80,
                }}
                onClick={() => {
                  setExpanded(false);
                }}>
                cancel
              </Button>
              <Button
                type='primary'
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: 'inline-flex',
                  width: '48%',
                  height: 80,
                }}
                loading={partyUpdateLoading}
                disabled={streamType === StreamType.Twitch && (!twitchId || twitchId.length < 4)}
                onClick={() => {
                  const variables = {
                    key: party.key,
                    streamType,
                    twitchId,
                    youtubeId,
                    mainParticipantId:
                      streamType === StreamType.Screenshare ? user?.uid : mainParticipantId,
                    backroomStreamId,
                    audioSource,
                    links: (form.getFieldValue('links') || []).map(
                      ({ text, url }: { text?: string; url: string }) => ({ text, url })
                    ),
                  };
                  updateParty({
                    variables,
                  }).then(() => {
                    setExpanded(false);
                    if (streamType === StreamType.Screenshare && !isSharingScreen) {
                      dispatch({ type: ActionType.SetShareScreen, isSharingScreen: true });
                    }
                  });
                }}>
                Update
              </Button>
            </div>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default HostControls;
